import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import callApi from '../../../utils/callApi';
import { Redirect } from '../../../utils/checkoutState';
import { config, getAssetUrl } from '../../../utils/config';
import onBackToShopClick from '../../../utils/onBackToShopClick';
import redirect from '../../../utils/redirect';
import sendDatadogEvent from '../../../utils/sendDatadogEvent';
import { pushErrorTrackingEventFromErrorObject, pushTrackingEvent } from '../../../utils/tracking';
import { wait } from '../../../utils/wait';
import Button from '../../atoms/Button';
import SubHeadline from '../../atoms/SubHeadline';
import { showDefaultError } from '../../molecules/Notifications';
import './style.scss';

export type PaymentConfirmationPollingResponse = {
  status: 'pending' | 'success' | 'failed';
  redirect?: Redirect;
};

const PaymentConfirmationPending = () => {
  const [status, setStatus] = useState<'pending' | 'timeout'>('pending');

  useEffect(() => {
    pushTrackingEvent({
      event: 'content_view',
      content_name: `${window.location.pathname}/payment-confirmation-pending`,
      search: window.location.search || null,
      app_version: 'v3',
      appId: config.appId,
      page_type: 'checkout:payment_confirmation_pending'
    });

    (async () => {
      let shouldPoll = true;
      let hasTimedOut = false;
      let pollInterval = 1000;
      setTimeout(() => (hasTimedOut = true), config.paymentConfirmationPollTimeout);
      setTimeout(() => (pollInterval = 5000), 20000); // After 20 seconds, increase polling interval from 1 to 5 seconds

      while (shouldPoll) {
        try {
          const paymentConfirmationPollingResponse = await callApi<PaymentConfirmationPollingResponse>({
            endpoint: '/state/order/confirmation/status',
            allowedStatusCodes: [424]
          });

          if (paymentConfirmationPollingResponse.status === 'success') {
            sendDatadogEvent('checkout-redirect', undefined, 'payment-pending-success');
            redirect(paymentConfirmationPollingResponse.redirect!);
            shouldPoll = false;
          } else if (paymentConfirmationPollingResponse.status === 'failed') {
            const url = new URL(window.location.href);
            url.searchParams.delete('waitForPaymentConfirmation');
            sendDatadogEvent('checkout-redirect', undefined, 'payment-pending-failed');
            redirect({ method: 'GET', url: url.toString() });
            shouldPoll = false;
          } else if (hasTimedOut) {
            setStatus('timeout');
            sendDatadogEvent('checkout-redirect', undefined, 'payment-pending-timeout');
            shouldPoll = false;
          }
        } catch (e) {
          console.error(e);
          showDefaultError();
          pushErrorTrackingEventFromErrorObject(e, 'poll_payment_confirmation');
        }
        await wait(pollInterval);
      }
    })();
  }, []);

  return (
    <>
      <div className="payment-status-container">
        {status === 'pending' && (
          <div>
            <img className="img-responsive" src={getAssetUrl('payment/confirmationPending/pending.gif')} />
            <SubHeadline>
              <FormattedMessage id="payment.status.pending" />
            </SubHeadline>
          </div>
        )}
        {status === 'timeout' && (
          <div>
            <img className="img-responsive" src={getAssetUrl('payment/confirmationPending/pending.gif')} />
            <SubHeadline>
              <FormattedMessage id="payment.status.timeout" />
            </SubHeadline>
            <Button onClick={onBackToShopClick}>
              <FormattedMessage id="auth.backToShopping" />
            </Button>
          </div>
        )}
      </div>
    </>
  );
};

export default PaymentConfirmationPending;
